/**
 * SkillManager — Singleton that controls the SkillTreePanel lifecycle.
 * Handles open/close/toggle, the K hotkey via EventBus, and debug hooks.
 *
 * Pattern matches InventoryManager exactly.
 *
 * @module SkillManager
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import CharacterSystem from '../systems/CharacterSystem.js';

// ─── SkillSystem — graceful lazy import ───────────────────────────────────────

let _skillSystem = null;
async function getSkillSystem() {
  if (_skillSystem) return _skillSystem;
  try {
    const mod = await import('../systems/SkillSystem.js');
    _skillSystem = mod.default;
    return _skillSystem;
  } catch (err) {
    DebugLog.warn(`[skill] SkillSystem not available for SkillManager: ${err.message}`);
    return null;
  }
}

// ─── SkillManager ─────────────────────────────────────────────────────────────

const SkillManager = {
  /** @type {import('./SkillTreePanel.js').default|null} */
  _panel: null,

  /** @type {boolean} Whether the skill tree panel is currently open */
  panelOpen: false,

  /**
   * Initialize the manager.
   * Listens on EventBus for 'input:skills', wires debug hooks.
   */
  init() {
    EventBus.on('input:skills', ({ charId } = {}) => {
      this.toggle(charId);
    });

    this._registerDebugHooks();

    DebugLog.info('[ui] SkillManager initialized');
  },

  // ─── Panel access ─────────────────────────────────────────────────────────

  /**
   * Lazily create the SkillTreePanel the first time it is needed.
   * @returns {Promise<import('./SkillTreePanel.js').default>}
   */
  async _getPanel() {
    if (this._panel) return this._panel;
    const { default: SkillTreePanel } = await import('./SkillTreePanel.js');
    this._panel = new SkillTreePanel();
    return this._panel;
  },

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Open the skill tree panel for a specific character.
   * Defaults to the first P1 character if no ID is supplied.
   * @param {string} [charId]
   */
  async open(charId) {
    const panel = await this._getPanel();
    panel.show(charId ?? null);
    this.panelOpen = true;
    DebugLog.info(`[ui] SkillManager.open; charId=${charId ?? 'default'}`);
  },

  /**
   * Close the skill tree panel.
   */
  async close() {
    if (!this._panel) return;
    this._panel.hide();
    this.panelOpen = false;
    DebugLog.info('[ui] SkillManager.close');
  },

  /**
   * Toggle the skill tree panel.
   * @param {string} [charId]
   */
  async toggle(charId) {
    if (this._panel?.isVisible()) {
      await this.close();
    } else {
      await this.open(charId);
    }
  },

  // ─── Debug hooks ──────────────────────────────────────────────────────────

  _registerDebugHooks() {
    if (typeof window === 'undefined') return;

    // Expose window.__debugGetSkills for external use
    window.__debugGetSkills = (nameOrId) => {
      let char = null;
      // Always prefer the live CharacterSystem reference to avoid reading stale copies
      if (nameOrId) {
        char = CharacterSystem.getById?.(nameOrId)
          ?? CharacterSystem.getAll?.().find(c => c.name?.toLowerCase() === String(nameOrId).toLowerCase())
          ?? null;
      }
      if (!char) {
        // Try partyState but resolve IDs to live objects
        const partyState = window.__partyState;
        if (partyState?.characters?.length > 0) {
          const first = partyState.characters[0];
          const id = typeof first === 'string' ? first : first?.id;
          if (id) char = CharacterSystem.getById?.(id) ?? null;
        }
        if (!char) {
          char = CharacterSystem.getAll?.().find(c => c.isHero)
            ?? CharacterSystem.getAll?.()[0]
            ?? null;
        }
      }
      if (!char) return null;
      return {
        character: char.name,
        skillPoints: char.skillPoints ?? 0,
        selectedTrees: char.skillTrees ?? [],
        skills: char.skills ?? {},
        feats: char.feats ?? [],
        equippedPartySkill: char.equippedPartySkill ?? null,
        activeAura: char.activeAura ?? null,
      };
    };

    // Wire into window.__debug (DebugConsole sets up the object first)
    const wireHooks = () => {
      if (!window.__debug) return;

      window.__debug.openSkills = (charId) => {
        this.open(charId);
        return `Skill tree opened${charId ? ` for ${charId}` : ''}`;
      };

      window.__debug.closeSkills = () => {
        this.close();
        return 'Skill tree closed';
      };

      window.__debug.getSkills = (charId) => {
        return window.__debugGetSkills?.(charId) ?? 'Not available';
      };

      window.__debug.spendSkillPoint = async (treeId, skillId, charId) => {
        const ss = await getSkillSystem();
        if (!ss) return 'SkillSystem not available';

        let char = null;
        if (charId) {
          char = CharacterSystem.getById?.(charId) ?? null;
        }
        if (!char) {
          // Try the currently visible panel's character first
          if (this._panel?.isVisible() && this._panel._char) {
            char = this._panel._char;
          } else {
            const partyState = window.__partyState;
            if (partyState?.characters?.length > 0) {
              const first = partyState.characters[0];
              char = typeof first === 'string' ? CharacterSystem.getById?.(first) : first;
            }
            if (!char) {
              char = CharacterSystem.getAll?.().find(c => c.isHero)
                ?? CharacterSystem.getAll?.()[0]
                ?? null;
            }
          }
        }

        if (!char) return 'No character found';

        let result;
        try {
          result = ss.spendPoint(char, treeId, skillId);
        } catch (err) {
          return `spendPoint error: ${err.message}`;
        }

        DebugLog.event(`[debug] spendSkillPoint; char=${char.name} tree=${treeId} skill=${skillId} success=${result?.success}`);

        // Refresh the panel if it's open
        if (this._panel?.isVisible()) {
          this._panel._renderAll();
        }

        return result?.success
          ? `Spent 1 point on ${skillId} in tree ${treeId} for ${char.name}`
          : `Failed: ${result?.message ?? 'unknown reason'}`;
      };
    };

    // Try immediately, and again after a short delay for late DebugConsole init
    wireHooks();
    setTimeout(wireHooks, 500);
  },
};

export default SkillManager;
